import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {

  LayoutDashboard, FolderTree, Plus, ChevronRight, ChevronDown, Sun, Moon, LogOut,
  Loader2, Building2, Shield, ShieldCheck, FileCheck, FlaskConical, Activity, Microscope,
  FileText, BarChart3, Settings, Beaker, ClipboardList, Target, Settings2,
  Table as TableIcon, Layers, Trash2, Info, Users
} from 'lucide-react';
import { fetchSln, THEMES } from '../nexus/nexusHelpers';
import { ServiceLifeNexus as SLNModule } from '../nexus/ServiceLifeNexus';
import { IdentityTesting as QCInner } from '../identity/IdentityTesting';
import MaSubmittalsGrid from './MaSubmittalsGrid';
import MaFamilySummary from './MaFamilySummary';
import MaSpecLibrary from './MaSpecLibrary';
import SpecCatalogPage from './SpecCatalogPage';
import PriorisQCItp, { PriorisQCItpSidebar } from './PriorisQCItp';
import { PriorisTeamPage, PriorisFirstLoginGate } from './PriorisTeam';
import { getApiBase } from '../../lib/apiBase';

const API = getApiBase();

/* ── QC tabs for sidebar ── */
const QC_TABS = [
  { id: 'project', label: 'Project Info', icon: ClipboardList },
  { id: 'mix', label: 'Mix Design', icon: Beaker },
  { id: 'samples', label: 'Samples & Results', icon: FlaskConical },
  { id: 'regime', label: 'Compliance Setup', icon: Settings2 },
  { id: 'projection', label: 'Strength Projection', icon: Target },
  { id: 'compliance', label: 'Compliance', icon: TableIcon },
  { id: 'analysis', label: 'Charts & Analysis', icon: BarChart3 },
  { id: 'report', label: 'Compliance Report', icon: FileText },
];

/* ── MA material categories with backend keys ── */
const MA_MATERIALS = [
  { id: 'concrete', label: 'Concrete', family: 'concrete_mix', children: [
    { id: 'aggregates',  label: 'Aggregates',   family: 'aggregates' },
    { id: 'cement',      label: 'Cement',       family: 'cement' },
    { id: 'scm',         label: 'SCMs (Fly ash, Silica fume, GGBS)', family: 'scm' },
    { id: 'water',       label: 'Water',        family: 'water' },
    { id: 'admixtures',  label: 'Admixtures',   family: 'admixtures' },
  ] },
  { id: 'steel',      label: 'Steel',         family: 'structural_steel', children: [
    { id: 'rebar',       label: 'Reinforcement (rebar)', family: 'rebar' },
  ] },
  { id: 'asphalt',    label: 'Asphalt',       family: 'asphalt',    children: [] },
  { id: 'earthworks', label: 'Earthworks',    family: 'earthworks', children: [] },
  { id: 'waterproofing', label: 'Waterproofing', family: 'waterproofing', children: [] },
  { id: 'grouting',   label: 'Grouting',      family: 'grouting',   children: [] },
];

export const PriorisApp = () => {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('dd-prioris-theme') || 'dark');
  const t = THEMES[theme];

  // Font scale slider — 80 % .. 130 %. Persisted in localStorage.
  const [fontScale, setFontScale] = useState(() => {
    const v = Number(localStorage.getItem('dd-prioris-font-scale'));
    return v >= 0.8 && v <= 1.3 ? v : 1;
  });
  useEffect(() => { localStorage.setItem('dd-prioris-font-scale', String(fontScale)); }, [fontScale]);

  // Tell the browser which palette to use for native widgets (scrollbars,
  // <option> lists, date pickers). Without this the OS paints them using the
  // document-level color-scheme which doesn't respect the in-app toggle.
  useEffect(() => {
    const prev = document.documentElement.style.colorScheme;
    document.documentElement.style.colorScheme = theme;
    return () => { document.documentElement.style.colorScheme = prev; };
  }, [theme]);

  // When fontScale < 1 the `zoom` transform below shrinks the whole app
  // container below viewport height, exposing the page body behind it (which
  // is white in default Tailwind). Paint the <html>/<body> with the current
  // theme so the leaked area stays themed, and restore on unmount.
  useEffect(() => {
    const prevHtml = document.documentElement.style.background;
    const prevBody = document.body.style.background;
    document.documentElement.style.background = t.bg;
    document.body.style.background = t.bg;
    return () => {
      document.documentElement.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, [t.bg]);

  // Module access
  const [moduleAccess, setModuleAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);

  // Portfolio / project hierarchy
  const [portfolios, setPortfolios] = useState([]);
  const [expandedPortfolio, setExpandedPortfolio] = useState(null);
  const [portfolioProjects, setPortfolioProjects] = useState({});

  // Navigation state
  const [activeView, setActiveView] = useState('dashboard'); // dashboard | portfolio | project-module
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeModule, setActiveModule] = useState(null); // 'approval' | 'qc' | 'service_life'
  const [expandedModule, setExpandedModule] = useState(null); // which module section is expanded
  const [qcTab, setQcTab] = useState('project');
  const [expandedMaMaterial, setExpandedMaMaterial] = useState(null);
  // Collapsible MA sidebar sections and tables — persist open-state per key
  const [maSidebarOpen, setMaSidebarOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dd-prioris-ma-sidebar-open') || '{}'); }
    catch { return {}; }
  });
  const toggleMaSidebar = (key) => setMaSidebarOpen(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem('dd-prioris-ma-sidebar-open', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });
  const [qcDrillAsset, setQcDrillAsset] = useState(null);
  const [qcDrillMaterial, setQcDrillMaterial] = useState(null);
  // ── QC · ITP sidebar state ────────────────────────────────────────────
  const [qcItpPours, setQcItpPours] = useState([]);
  const [qcItpPourId, setQcItpPourId] = useState(null);
  const [qcItpNewSignal, setQcItpNewSignal] = useState(0);
  const [qcApplicableItps, setQcApplicableItps] = useState([]);      // [{code, label_short, control_points, ...}]
  const [qcDrillCheckpoint, setQcDrillCheckpoint] = useState(null);  // {itp_key, cp}
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('dd-prioris-sidebar') === '1');
  const [maFamily, setMaFamily] = useState(null);    // {key, label, family, spec_table_key?, spec_rubrica_code?}
  const [maSpecView, setMaSpecView] = useState(false); // when true, Spec Library renders
  const [maSpecSelections, setMaSpecSelections] = useState([]); // project spec-catalog selections
  const [maShowLog, setMaShowLog] = useState(false); // when true, show the all-submittals log view
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [newAssetForPortfolio, setNewAssetForPortfolio] = useState(null);
  // Portfolio detail view in the main pane (Service Life-style)
  const [selectedPortfolioDetail, setSelectedPortfolioDetail] = useState(null);

  useEffect(() => { localStorage.setItem('dd-prioris-sidebar', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);

  useEffect(() => { localStorage.setItem('dd-prioris-theme', theme); }, [theme]);
  useEffect(() => {
    if (!authLoading && !user) navigate('/auth/login?redirect=/engineering/prioris-app');
  }, [user, authLoading, navigate]);

  // Load module access
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('dd-token');
    fetch(`${API}/api/prioris/module-access`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(setModuleAccess)
      .catch(() => setModuleAccess({ materials_approval: true, quality_control: true, sensors_monitoring: false, service_life: false }))
      .finally(() => setAccessLoading(false));
  }, [user]);

  // Load portfolios
  const loadPortfolios = useCallback(async () => {
    try {
      const data = await fetchSln('/api/sln/portfolios');
      setPortfolios(data);
    } catch { setPortfolios([]); }
  }, []);

  useEffect(() => { if (user) loadPortfolios(); }, [user, loadPortfolios]);

  // Listen for portfolio/asset changes from the SLN module (or elsewhere) and refresh
  useEffect(() => {
    const refresh = async () => {
      await loadPortfolios();
      // also refresh any expanded portfolio's assets so new rows appear immediately
      if (expandedPortfolio) {
        try {
          const data = await fetchSln(`/api/sln/portfolios/${expandedPortfolio}/assets`);
          setPortfolioProjects(prev => ({ ...prev, [expandedPortfolio]: data }));
        } catch {}
      }
    };
    window.addEventListener('prioris-portfolios-changed', refresh);
    return () => window.removeEventListener('prioris-portfolios-changed', refresh);
  }, [loadPortfolios, expandedPortfolio]);

  // Load projects (assets) for a portfolio
  const loadProjects = async (portfolioId) => {
    try {
      const data = await fetchSln(`/api/sln/portfolios/${portfolioId}/assets`);
      setPortfolioProjects(prev => ({ ...prev, [portfolioId]: data }));
    } catch {
      setPortfolioProjects(prev => ({ ...prev, [portfolioId]: [] }));
    }
  };

  const togglePortfolio = (pId) => {
    if (expandedPortfolio === pId) {
      setExpandedPortfolio(null);
    } else {
      setExpandedPortfolio(pId);
      if (!portfolioProjects[pId]) loadProjects(pId);
    }
  };

  const selectProject = (project, portfolioName) => {
    setSelectedProject(project);
    setSelectedPortfolio(portfolioName);
    setActiveView('project-module');
    setActiveModule(null);
    setExpandedModule(null);
  };

  const goToDashboard = () => {
    setActiveView('dashboard');
    setSelectedProject(null);
    setSelectedPortfolioDetail(null);
    setActiveModule(null);
    setExpandedModule(null);
    setQcDrillAsset(null);
    setQcDrillMaterial(null);
  };

  // Open a portfolio's SL-style detail page in the main pane.
  const openPortfolioDetail = (p) => {
    setSelectedPortfolioDetail(p);
    setSelectedProject(null);
    setActiveModule(null);
    setExpandedModule(null);
    setActiveView('portfolio-detail');
    if (!portfolioProjects[p.id]) loadProjects(p.id);
    if (expandedPortfolio !== p.id) setExpandedPortfolio(p.id);
  };

  // Delete portfolio / asset (mirrors Service Life's /api/sln DELETEs).
  const deleteItem = async (type, id) => {
    const map = { portfolio: 'portfolios', asset: 'assets' };
    if (!window.confirm(`Delete this ${type}? This cannot be undone.`)) return;
    try {
      await fetchSln(`/api/sln/${map[type]}/${id}`, { method: 'DELETE' });
      if (type === 'portfolio') {
        if (selectedPortfolioDetail?.id === id) setSelectedPortfolioDetail(null);
        if (expandedPortfolio === id) setExpandedPortfolio(null);
        await loadPortfolios();
        goToDashboard();
      } else {
        if (selectedProject?.id === id) setSelectedProject(null);
        const pid = expandedPortfolio || selectedPortfolioDetail?.id;
        if (pid) await loadProjects(pid);
        await loadPortfolios();
      }
      window.dispatchEvent(new CustomEvent('prioris-portfolios-changed'));
    } catch (e) { alert(e.message || 'Delete failed'); }
  };

  // Refresh the project's spec-catalog selections (used as the MA sidebar source).
  const reloadSpecSelections = () => {
    if (!selectedProject?.id) { setMaSpecSelections([]); return; }
    const token = localStorage.getItem('dd-token');
    fetch(`${API}/api/prioris/spec-catalog/project/${selectedProject.id}/selections`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { selections: [] })
      .then(d => setMaSpecSelections(d.selections || []))
      .catch(() => setMaSpecSelections([]));
  };
  useEffect(() => { reloadSpecSelections(); }, [selectedProject?.id, activeModule]);

  // Fetch QC ITP pours + applicable ITP catalog for the sidebar mini-list
  // whenever the user drills into QC for an asset.
  useEffect(() => {
    if (activeModule !== 'qc' || !qcDrillAsset?.id) return;
    const token = localStorage.getItem('dd-token');
    const load = async () => {
      // Applicable ITP catalog (driven by project spec selections)
      try {
        const r = await fetch(`${API}/api/prioris/qc/itp/catalog?project_id=${qcDrillAsset.id}`,
          { headers: { Authorization: `Bearer ${token}` } });
        const d = r.ok ? await r.json() : { itps: [] };
        setQcApplicableItps((d.itps || []).filter(x => x.applicable));
      } catch { setQcApplicableItps([]); }
      // Pours (only needed when a pouring-record checkpoint is open)
      try {
        const r = await fetch(`${API}/api/prioris/qc/itp/pours?project_id=${qcDrillAsset.id}`,
          { headers: { Authorization: `Bearer ${token}` } });
        const d = r.ok ? await r.json() : { pours: [] };
        setQcItpPours(d.pours || []);
      } catch { setQcItpPours([]); }
    };
    load();
    const handler = () => load();
    window.addEventListener('prioris-qc-pours-changed', handler);
    return () => window.removeEventListener('prioris-qc-pours-changed', handler);
  }, [activeModule, qcDrillAsset?.id]);

  // Reset ITP/checkpoint selection when leaving the module
  useEffect(() => {
    if (activeModule !== 'qc') {
      setQcItpPourId(null);
      setQcDrillCheckpoint(null);
    }
  }, [activeModule]);

  if (authLoading || accessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: t.accent }} />
      </div>
    );
  }
  if (!user) return null;

  // First-login gate: users with `must_change_password` cannot access the shell
  if (user.must_change_password) {
    return <PriorisFirstLoginGate t={t} user={user} onComplete={() => refreshUser && refreshUser()} />;
  }

  const trialDays = user.trial_expires_at ? Math.max(0, Math.ceil((new Date(user.trial_expires_at) - new Date()) / 86400000)) : 35;

  return (
    <div className={`flex prioris-app prioris-theme-${theme}`} style={{
      background: t.bg,
      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      zoom: fontScale,
      // Compensate for `zoom` below 100 % — logical height must grow so the
      // post-zoom visual height still equals (or exceeds) one viewport.
      minHeight: `${100 / fontScale}vh`,
    }}>
      {/* ══════ SIDEBAR ══════ */}
      <aside className={`${sidebarCollapsed ? 'w-12' : 'w-56'} flex-shrink-0 h-screen sticky top-0 overflow-y-auto overflow-x-hidden border-r flex flex-col transition-all duration-200`}
        style={{ background: t.sidebar, borderColor: t.cardBorder }} data-testid="prioris-sidebar">

        {/* Brand — click to collapse/expand */}
        <button
          onClick={() => setSidebarCollapsed(v => !v)}
          className="p-3 pb-1 text-left hover:opacity-80 transition-opacity"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="prioris-sidebar-toggle"
        >
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.12)' }}>
              <Shield className="w-3 h-3" style={{ color: t.accent }} />
            </div>
            {!sidebarCollapsed && <span className="text-xs font-bold tracking-wide" style={{ color: t.text }}>Prioris</span>}
          </div>
          {!sidebarCollapsed && (
            <p className="text-[10px] mt-1 pl-7" style={{ color: t.textDim }}>
              {trialDays > 0 ? `${trialDays}d trial` : 'Trial expired'}
            </p>
          )}
        </button>

        <nav className={`flex-1 ${sidebarCollapsed ? 'px-1' : 'px-1.5'} py-1 overflow-y-auto overflow-x-hidden text-[12px]`}>
          {/* Dashboard */}
          <SideItem icon={LayoutDashboard} label="Dashboard" active={activeView === 'dashboard'} onClick={goToDashboard} t={t} collapsed={sidebarCollapsed} />

          {/* Portfolios — hidden when collapsed */}
          {!sidebarCollapsed && (
            <>
              <div className="mt-2 mb-1 px-2 flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: t.textDim }}>Portfolios</span>
                <button onClick={() => setShowNewPortfolio(true)}
                  className="p-0.5 rounded hover:bg-white/5"
                  title="New portfolio"
                  style={{ color: t.accent }}
                  data-testid="btn-new-portfolio">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              {portfolios.map(p => (
                <div key={p.id}>
                  <div className="group flex items-center gap-0.5 pr-1 rounded transition-colors hover:bg-white/5"
                    style={{ color: selectedPortfolioDetail?.id === p.id ? t.accent : t.textMuted, background: selectedPortfolioDetail?.id === p.id ? t.accentBg : 'transparent' }}
                  >
                    <button onClick={() => togglePortfolio(p.id)}
                      className="p-1 flex-shrink-0"
                      title={expandedPortfolio === p.id ? 'Collapse' : 'Expand'}
                      data-testid={`portfolio-toggle-${p.id}`}>
                      {expandedPortfolio === p.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </button>
                    <button onClick={() => openPortfolioDetail(p)}
                      className="flex-1 flex items-center gap-1.5 py-1.5 text-left min-w-0"
                      data-testid={`portfolio-${p.id}`}>
                      <FolderTree className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate font-medium">{p.portfolio_name || p.name || 'Untitled'}</span>
                    </button>
                    <button onClick={() => deleteItem('portfolio', p.id)}
                      className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                      title="Delete portfolio"
                      style={{ color: '#ef4444' }}
                      data-testid={`portfolio-delete-${p.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {expandedPortfolio === p.id && (
                    <div className="ml-3 border-l" style={{ borderColor: t.cardBorder }}>
                      {(portfolioProjects[p.id] || []).map(asset => (
                        <div key={asset.id}
                          className="group flex items-center gap-0.5 pr-1 rounded transition-colors hover:bg-white/5"
                          style={{ color: selectedProject?.id === asset.id ? t.accent : t.textMuted, background: selectedProject?.id === asset.id ? t.accentBg : 'transparent' }}
                        >
                          <button onClick={() => selectProject(asset, p.portfolio_name || p.name)}
                            className="flex-1 flex items-center gap-1.5 px-2 py-1 text-left min-w-0"
                            data-testid={`project-${asset.id}`}>
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{asset.asset_name || asset.name || 'Untitled'}</span>
                          </button>
                          <button onClick={() => deleteItem('asset', asset.id)}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                            title="Delete asset"
                            style={{ color: '#ef4444' }}
                            data-testid={`asset-delete-${asset.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      {(portfolioProjects[p.id] || []).length === 0 && (
                        <p className="px-3 py-1 text-[11px]" style={{ color: t.textDim }}>No projects</p>
                      )}
                      <button
                        onClick={() => setNewAssetForPortfolio(p)}
                        className="w-full flex items-center gap-1 px-2 py-1 rounded transition-colors hover:opacity-80 text-[11px]"
                        style={{ color: t.accent }}
                        data-testid={`btn-new-asset-${p.id}`}
                      >
                        <Plus className="w-2.5 h-2.5" /> New asset
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {portfolios.length === 0 && (
                <p className="px-2 py-2 text-[11px]" style={{ color: t.textDim }}>No portfolios yet</p>
              )}
            </>
          )}

          {/* Module nav (always visible when portfolios exist) */}
          {portfolios.length > 0 && (
            <>
              {!sidebarCollapsed && (
                <div className="mt-3 mb-1 px-2 border-t pt-2" style={{ borderColor: t.cardBorder }}>
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: t.textDim }}>
                    {selectedProject ? (selectedProject.asset_name || selectedProject.name || 'Project') : 'Modules'}
                  </span>
                </div>
              )}

              {/* Spec Library (shared across all material modules) */}
              <div>
                <SideItem icon={ShieldCheck} label="Spec Library" active={activeModule === 'spec_catalog'} onClick={() => { setActiveModule('spec_catalog'); setExpandedModule(null); setQcDrillAsset(null); setQcDrillMaterial(null); setMaFamily(null); setMaSpecView(false); }} t={t} collapsed={sidebarCollapsed} testId="nav-spec-catalog" />
              </div>

              {/* Materials Approval */}
              {moduleAccess?.materials_approval && (
                <div>
                  <SideItem icon={FileCheck} label="Materials Approval" active={activeModule === 'approval'} onClick={() => { setActiveModule('approval'); setExpandedModule(expandedModule === 'approval' ? null : 'approval'); setQcDrillAsset(null); setQcDrillMaterial(null); setMaFamily(null); setMaSpecView(false); }} t={t} chevron expandedModule={expandedModule} moduleId="approval" collapsed={sidebarCollapsed} />
                  {!sidebarCollapsed && expandedModule === 'approval' && (
                    <div className="ml-4 border-l" style={{ borderColor: t.cardBorder }}>
                      {/* All submittals log entry (always visible) */}
                      {selectedProject?.id && (
                        <button
                          onClick={() => { setMaShowLog(true); setMaFamily(null); setMaSpecView(false); setActiveModule('approval'); }}
                          className="w-full flex items-center gap-1.5 px-2 py-1 rounded transition-colors hover:opacity-80"
                          style={{
                            color: maShowLog ? t.accent : t.textMuted,
                            background: maShowLog ? t.accentBg : 'transparent',
                          }}
                          data-testid="ma-all-submittals">
                          <FileCheck className="w-2.5 h-2.5 flex-shrink-0" />
                          <span className="truncate text-[11px] font-medium">All submittals</span>
                        </button>
                      )}
                      {(() => {
                        // Dynamic MA sidebar: sourced from project spec-catalog selections.
                        if (!selectedProject?.id) {
                          return <div className="px-2 py-2 text-[10px]" style={{ color: t.textDim }}>Select a project first.</div>;
                        }
                        if (!maSpecSelections || maSpecSelections.length === 0) {
                          return (
                            <div className="px-2 py-2 text-[10px] space-y-1" style={{ color: t.textDim }}>
                              <div>No project specs selected yet.</div>
                              <button onClick={() => { setActiveModule('spec_catalog'); setMaFamily(null); setMaSpecView(false); }}
                                className="underline" style={{ color: t.accent }} data-testid="ma-go-speclib">
                                → Open Spec Library
                              </button>
                            </div>
                          );
                        }
                        // Group rubricas by table.section_key → table_key
                        const volumes = {};
                        for (const sel of maSpecSelections) {
                          const vKey = sel.section_key || '_other';
                          volumes[vKey] = volumes[vKey] || { label: vKey, tables: {} };
                          const tKey = sel.table_key;
                          volumes[vKey].tables[tKey] = volumes[vKey].tables[tKey] || {
                            key: tKey, label: sel.table_label, family: sel.family, rubricas: sel.rubrica_details || [],
                          };
                        }
                        return Object.entries(volumes).map(([vKey, vol]) => {
                          const sectionOpen = maSidebarOpen[`sec:${vKey}`] !== false; // default open
                          return (
                            <div key={vKey} className="mb-1">
                              <button
                                onClick={() => toggleMaSidebar(`sec:${vKey}`)}
                                className="w-full flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider hover:opacity-80"
                                style={{ color: t.accent }}
                                data-testid={`ma-sec-toggle-${vKey}`}>
                                <ChevronRight
                                  className="w-3 h-3 flex-shrink-0 transition-transform"
                                  style={{ transform: sectionOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                />
                                <span className="truncate">{vKey}</span>
                              </button>
                              {sectionOpen && Object.values(vol.tables).map(tbl => {
                                const tblOpen = maSidebarOpen[`tbl:${tbl.key}`] === true; // default CLOSED per user request
                                return (
                                  <div key={tbl.key} className="ml-1">
                                    <button
                                      onClick={() => toggleMaSidebar(`tbl:${tbl.key}`)}
                                      className="w-full flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono hover:opacity-80 text-left"
                                      style={{ color: t.textMuted }}
                                      title={tbl.label}
                                      data-testid={`ma-tbl-toggle-${tbl.key}`}>
                                      <ChevronRight
                                        className="w-3 h-3 flex-shrink-0 transition-transform"
                                        style={{ transform: tblOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                      />
                                      <span className="truncate">{tbl.key}</span>
                                      <span className="ml-auto text-[9px] px-1 rounded"
                                        style={{ background: t.inputBg, color: t.textDim }}>{tbl.rubricas.length}</span>
                                    </button>
                                    {tblOpen && (
                                      <div className="ml-4">
                                        {tbl.rubricas.map(r => {
                                          const mkey = `${tbl.key}::${r.code}`;
                                          const active = maFamily?.key === mkey;
                                          return (
                                            <button key={r.code}
                                              onClick={() => {
                                                setMaFamily({
                                                  key: mkey,
                                                  label: r.label,
                                                  family: tbl.family,
                                                  spec_table_key: tbl.key,
                                                  spec_rubrica_code: r.code,
                                                  spec_rubrica_label: r.label,
                                                });
                                                setMaSpecView(false);
                                                setMaShowLog(false);
                                                setActiveModule('approval');
                                              }}
                                              className="w-full flex items-center gap-1 px-2 py-0.5 rounded transition-colors hover:opacity-80 text-left"
                                              style={{
                                                color: active ? t.accent : t.textDim,
                                                background: active ? t.accentBg : 'transparent',
                                              }}
                                              data-testid={`ma-rubrica-${r.code}`}>
                                              <span className="font-mono text-[10px] flex-shrink-0" style={{ opacity: 0.75 }}>{r.code}</span>
                                              <span className="truncate text-[11px]">{r.label}</span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Quality Control */}
              {moduleAccess?.quality_control && (
                <div>
                  <SideItem icon={FlaskConical} label="Quality Control" active={activeModule === 'qc'} onClick={() => { const wasOnQC = activeModule === 'qc'; setActiveModule('qc'); setExpandedModule(expandedModule === 'qc' ? null : 'qc'); if (!wasOnQC) { setQcDrillAsset(selectedProject || null); setQcDrillMaterial(null); setQcDrillCheckpoint(null); } }} t={t} chevron expandedModule={expandedModule} moduleId="qc" collapsed={sidebarCollapsed} />
                  {!sidebarCollapsed && expandedModule === 'qc' && qcDrillAsset && (
                    <div className="ml-4 border-l" style={{ borderColor: t.cardBorder }}>
                      {/* "ITPs" section header */}
                      <div className="px-2 py-1 flex items-center gap-1" style={{ color: t.textDim }}>
                        <ClipboardList className="w-2.5 h-2.5" />
                        <span className="text-[10px] font-bold tracking-[0.1em] uppercase">ITPs</span>
                      </div>
                      {qcApplicableItps.length === 0 ? (
                        <p className="px-3 py-1 text-[10px] italic" style={{ color: t.textDim }}>
                          No spec applied yet — go to Spec Library.
                        </p>
                      ) : (
                        qcApplicableItps.map(itp => (
                          <div key={itp.code} className="ml-2">
                            <div className="px-2 py-0.5 text-[10px] font-semibold" style={{ color: t.text }}>
                              {itp.label_short || itp.code}
                            </div>
                            <div className="ml-2 border-l" style={{ borderColor: t.cardBorder }}>
                              {(itp.control_points || []).map(cp => {
                                const isActive = qcDrillCheckpoint?.itp_key === itp.code && qcDrillCheckpoint?.cp?.code === cp.code;
                                return (
                                  <button key={cp.code}
                                    onClick={() => { setQcDrillCheckpoint({ itp_key: itp.code, cp }); setQcDrillMaterial(null); }}
                                    className="w-full text-left px-2 py-0.5 rounded flex items-start gap-1 transition-colors hover:bg-white/5"
                                    style={{ color: isActive ? t.accent : t.textDim, background: isActive ? t.accentBg : 'transparent' }}
                                    data-testid={`qc-cp-${cp.code}`}
                                    title={cp.label}>
                                    <span className="font-mono text-[9px] mt-0.5 flex-shrink-0" style={{ color: isActive ? t.accent : t.textMuted }}>
                                      {cp.code.replace(/^\d+-/, '')}
                                    </span>
                                    <span className="truncate text-[10px]">{cp.label_short || cp.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      )}
                      {/* Mini-ITP pours list (only when the pouring-record CP is active) */}
                      {qcDrillCheckpoint?.cp?.kind === 'pouring_record' && (
                        <div className="mt-2 pt-2 border-t" style={{ borderColor: t.cardBorder }}>
                          <div className="px-2 pb-0.5 text-[9px] font-bold tracking-[0.1em] uppercase" style={{ color: t.textDim }}>
                            Pours
                          </div>
                          <PriorisQCItpSidebar
                            t={t}
                            projectId={qcDrillAsset.id}
                            pours={qcItpPours}
                            selectedPourId={qcItpPourId}
                            onSelectPour={(pid) => setQcItpPourId(pid)}
                            onNewPour={() => { setQcItpPourId(null); setQcItpNewSignal(s => s + 1); }}
                            onAllPours={() => setQcItpPourId(null)}
                          />
                        </div>
                      )}
                      {/* Strength module sub-tabs (when the strength CP is active) */}
                      {qcDrillCheckpoint?.cp?.kind === 'strength_statistical' && (
                        <div className="mt-2 pt-2 border-t" style={{ borderColor: t.cardBorder }}>
                          <div className="px-2 pb-0.5 text-[9px] font-bold tracking-[0.1em] uppercase" style={{ color: t.textDim }}>
                            Strength analysis
                          </div>
                          <div className="ml-2">
                            {QC_TABS.map(tab => (
                              <button key={tab.id} onClick={() => setQcTab(tab.id)}
                                className="w-full flex items-center gap-1.5 px-2 py-0.5 rounded transition-colors"
                                style={{ color: qcTab === tab.id ? t.accent : t.textDim, background: qcTab === tab.id ? t.accentBg : 'transparent' }}
                                data-testid={`qc-tab-${tab.id}`}>
                                <tab.icon className="w-2.5 h-2.5 flex-shrink-0" />
                                <span className="truncate text-[10px]">{tab.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Sensors & Monitoring (stealth) */}
              {moduleAccess?.sensors_monitoring && (
                <SideItem icon={Activity} label="Sensors" active={activeModule === 'sensors'} onClick={() => { setActiveModule('sensors'); setQcDrillAsset(null); setQcDrillMaterial(null); }} t={t} badge="beta" collapsed={sidebarCollapsed} />
              )}

              {/* Service Life (stealth) */}
              {moduleAccess?.service_life && (
                <SideItem icon={Microscope} label="Service Life" active={activeModule === 'service_life'} onClick={() => { setActiveModule('service_life'); setQcDrillAsset(null); setQcDrillMaterial(null); }} t={t} badge="beta" collapsed={sidebarCollapsed} />
              )}
            </>
          )}

          {/* Bottom utils */}
          <div className="mt-3 border-t pt-2" style={{ borderColor: t.cardBorder }}>
            <SideItem icon={FileText} label="Documents" active={activeView === 'documents'} onClick={() => { setActiveView('documents'); setActiveModule(null); }} t={t} collapsed={sidebarCollapsed} />
            <SideItem icon={BarChart3} label="Reports" active={activeView === 'reports'} onClick={() => { setActiveView('reports'); setActiveModule(null); }} t={t} collapsed={sidebarCollapsed} />
            <SideItem icon={Users} label="Team" active={activeView === 'team'} onClick={() => { setActiveView('team'); setActiveModule(null); }} t={t} collapsed={sidebarCollapsed} testId="nav-team" />
            <SideItem icon={Settings} label="Settings" active={activeView === 'settings'} onClick={() => { setActiveView('settings'); setActiveModule(null); }} t={t} collapsed={sidebarCollapsed} />
          </div>
        </nav>

        {/* Footer */}
        <div className="p-2 border-t" style={{ borderColor: t.cardBorder }}>
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-1' : 'justify-between mb-1'}`}>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-1 rounded" style={{ background: t.inputBg }} data-testid="prioris-theme-toggle">
              {theme === 'dark' ? <Sun className="w-3 h-3" style={{ color: t.textMuted }} /> : <Moon className="w-3 h-3" style={{ color: t.textMuted }} />}
            </button>
            <button onClick={() => { logout(); navigate('/auth/login'); }} className="p-1 rounded" style={{ background: t.inputBg }} data-testid="prioris-logout">
              <LogOut className="w-3 h-3" style={{ color: t.textMuted }} />
            </button>
          </div>
          {!sidebarCollapsed && <p className="text-[10px] truncate" style={{ color: t.textDim }}>{user.email}</p>}
        </div>
      </aside>

      {/* ══════ MAIN CONTENT ══════ */}
      <main className="flex-1 min-h-screen overflow-y-auto" style={{ background: t.bg }}>
        {!activeModule && activeView === 'dashboard' && <Dashboard t={t} portfolios={portfolios} moduleAccess={moduleAccess} onRefresh={loadPortfolios} onOpenPortfolio={openPortfolioDetail} onOpenAssetModule={(asset, modKey, portfolioName) => { setSelectedProject(asset); setSelectedPortfolio(portfolioName); setActiveModule(modKey); if (modKey === 'approval' || modKey === 'qc') setExpandedModule(modKey); if (modKey === 'qc') { setQcDrillAsset(asset); setQcDrillMaterial(null); } }} />}
        {activeView === 'portfolio-detail' && selectedPortfolioDetail && (
          <PortfolioDetailView
            t={t}
            portfolio={selectedPortfolioDetail}
            assets={portfolioProjects[selectedPortfolioDetail.id] || []}
            moduleAccess={moduleAccess}
            onBack={goToDashboard}
            onOpenAsset={(asset) => selectProject(asset, selectedPortfolioDetail.portfolio_name || selectedPortfolioDetail.name)}
            onOpenAssetModule={(asset, modKey) => { setSelectedProject(asset); setSelectedPortfolio(selectedPortfolioDetail.portfolio_name || selectedPortfolioDetail.name); setActiveModule(modKey); if (modKey === 'approval' || modKey === 'qc') setExpandedModule(modKey); if (modKey === 'qc') { setQcDrillAsset(asset); setQcDrillMaterial(null); } }}
            onDeletePortfolio={() => deleteItem('portfolio', selectedPortfolioDetail.id)}
            onDeleteAsset={(aid) => deleteItem('asset', aid)}
            onAssetsChanged={() => loadProjects(selectedPortfolioDetail.id)}
          />
        )}
        {activeModule === 'spec_catalog' && <SpecCatalogPage t={t} project={selectedProject} />}
        {activeModule === 'approval' && maSpecView && selectedProject && (
          <MaSpecLibrary t={t} project={selectedProject} onOpenPdfIngest={() => alert('Spec ingestion workflow — in development. It will support assisted extraction of clauses, test methods, limits and frequencies from uploaded project specs, with engineer review before saving.')} />
        )}
        {activeModule === 'approval' && !maSpecView && maShowLog && selectedProject && (
          <MaSubmittalsGrid t={t} project={selectedProject} specSelections={maSpecSelections} onBack={() => setMaShowLog(false)} />
        )}
        {activeModule === 'approval' && !maSpecView && !maShowLog && maFamily && <MaFamilySummary t={t} family={maFamily} projectId={selectedProject?.id || null} onBack={() => setMaFamily(null)} specRubricaCode={maFamily?.spec_rubrica_code} specTableKey={maFamily?.spec_table_key} specRubricaLabel={maFamily?.spec_rubrica_label} />}
        {activeModule === 'approval' && !maSpecView && !maShowLog && !maFamily && !selectedProject && <MAPortfolioDashboard t={t} onSelectProject={setSelectedProject} />}
        {activeModule === 'approval' && !maSpecView && !maShowLog && !maFamily && selectedProject && (
          maSpecSelections.length === 0
            ? <MANoSpecsPrompt t={t} onOpenLib={() => setActiveModule('spec_catalog')} />
            : <MAAssetDashboard t={t} project={selectedProject} specSelections={maSpecSelections} onPickRubrica={(tbl, r) => setMaFamily({ key: `${tbl.table_key}::${r.code}`, label: r.label, family: tbl.family, spec_table_key: tbl.table_key, spec_rubrica_code: r.code, spec_rubrica_label: r.label })} />
        )}
        {activeModule === 'qc' && !qcDrillAsset && <QCPortfolioDashboard t={t} onDrillAsset={setQcDrillAsset} />}
        {activeModule === 'qc' && qcDrillAsset && !qcDrillCheckpoint && (
          <QCSpecHub t={t} asset={qcDrillAsset} applicableItps={qcApplicableItps}
            onPickCheckpoint={(itpKey, cp) => setQcDrillCheckpoint({ itp_key: itpKey, cp })}
            onOpenSpecLib={() => setActiveModule('spec_catalog')} />
        )}
        {activeModule === 'qc' && qcDrillAsset && qcDrillCheckpoint?.cp?.kind === 'pouring_record' && (
          <PriorisQCItp
            t={t}
            project={qcDrillAsset}
            selectedPourId={qcItpPourId}
            newPourSignal={qcItpNewSignal}
            onPoursChanged={(pours) => setQcItpPours(pours)}
          />
        )}
        {activeModule === 'qc' && qcDrillAsset && qcDrillCheckpoint?.cp?.kind === 'strength_statistical' && (
          <QCInner embedded={true} forcedTab={qcTab} onTabChange={setQcTab} />
        )}
        {activeModule === 'qc' && qcDrillAsset && qcDrillCheckpoint?.cp?.kind === 'readonly' && (
          <PriorisQCCheckpointView t={t} itpKey={qcDrillCheckpoint.itp_key} cp={qcDrillCheckpoint.cp}
            onBack={() => setQcDrillCheckpoint(null)} />
        )}
        {activeModule === 'service_life' && <SLNModule embedded={true} />}
        {activeModule === 'sensors' && <SensorsPlaceholder t={t} />}
        {!activeModule && activeView === 'project-module' && selectedProject && <ProjectOverview t={t} project={selectedProject} portfolio={selectedPortfolio} moduleAccess={moduleAccess} onSelectModule={(m) => { setActiveModule(m); if (m === 'qc') { setQcDrillAsset(selectedProject); setQcDrillMaterial(null); setQcDrillCheckpoint(null); } }} onExpandModule={setExpandedModule} onDelete={() => deleteItem('asset', selectedProject.id)} />}
        {!activeModule && activeView === 'settings' && <PriorisSettingsView t={t} theme={theme} setTheme={setTheme} fontScale={fontScale} setFontScale={setFontScale} />}
        {!activeModule && activeView === 'team' && selectedProject && <PriorisTeamPage t={t} project={selectedProject} />}
        {!activeModule && activeView === 'team' && !selectedProject && (
          <div className="p-6 max-w-4xl mx-auto text-[12px]" style={{ color: t.text }}>
            <h2 className="text-lg font-bold mb-2">Team</h2>
            <p style={{ color: t.textDim }}>Pick a project in the sidebar to manage its team.</p>
          </div>
        )}
        {!activeModule && ['documents', 'reports'].includes(activeView) && <GenericPlaceholder label={activeView.charAt(0).toUpperCase() + activeView.slice(1)} t={t} />}
      </main>

      {showNewPortfolio && <NewPortfolioModal t={t} onClose={() => setShowNewPortfolio(false)} onCreated={() => { setShowNewPortfolio(false); loadPortfolios(); }} />}
      {newAssetForPortfolio && <NewAssetModal t={t} portfolio={newAssetForPortfolio} onClose={() => setNewAssetForPortfolio(null)} onCreated={() => { const p = newAssetForPortfolio; setNewAssetForPortfolio(null); loadPortfolios(); }} />}
    </div>
  );
};

function NewAssetModal({ t, portfolio, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [assetType, setAssetType] = useState('building');
  const [location, setLocation] = useState('');
  const [year, setYear] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try {
      const token = localStorage.getItem('dd-token');
      const body = {
        asset_name: name.trim(),
        asset_code: code.trim() || undefined,
        asset_type: assetType,
        location: location.trim() || undefined,
        commissioning_year: year ? parseInt(year) : undefined,
        description: desc.trim() || undefined,
      };
      const res = await fetch(`${API}/api/sln/portfolios/${portfolio.id}/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg = typeof j.detail === 'string' ? j.detail : (Array.isArray(j.detail) ? j.detail.map(d => d.msg || JSON.stringify(d)).join(', ') : JSON.stringify(j.detail || 'Failed'));
        throw new Error(msg);
      }
      onCreated();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} data-testid="new-asset-modal">
      <div className="w-full max-w-md rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }} onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b" style={{ borderColor: t.cardBorder }}>
          <div className="text-[13px] font-bold">New asset</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>Under <strong>{portfolio.portfolio_name || portfolio.name}</strong></div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>NAME *</div>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              className="w-full rounded px-2 py-1 text-[12px]"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="new-asset-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>CODE (optional)</div>
              <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="auto"
                className="w-full rounded px-2 py-1 text-[12px] font-mono"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
            <div>
              <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>TYPE</div>
              <select value={assetType} onChange={e => setAssetType(e.target.value)}
                className="w-full rounded px-2 py-1 text-[12px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}>
                <option value="building">Building</option>
                <option value="bridge">Bridge</option>
                <option value="tunnel">Tunnel</option>
                <option value="road">Road / Pavement</option>
                <option value="hydraulic">Hydraulic</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>LOCATION</div>
              <input value={location} onChange={e => setLocation(e.target.value)}
                className="w-full rounded px-2 py-1 text-[12px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
            <div>
              <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>COMMISSIONING YEAR</div>
              <input type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="2024"
                className="w-full rounded px-2 py-1 text-[12px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>DESCRIPTION</div>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
              className="w-full rounded px-2 py-1 text-[12px]"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
          </div>
          {err && <div className="text-[11px]" style={{ color: '#f87171' }}>{err}</div>}
        </div>
        <div className="p-3 border-t flex gap-2 justify-end" style={{ borderColor: t.cardBorder }}>
          <button onClick={onClose} className="px-3 py-1 rounded text-[12px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
          <button onClick={save} disabled={busy}
            className="px-3 py-1 rounded text-[12px] font-semibold disabled:opacity-50"
            style={{ background: t.accent, color: '#000' }}
            data-testid="new-asset-save">
            {busy ? 'Creating…' : 'Create asset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewPortfolioModal({ t, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try {
      const token = localStorage.getItem('dd-token');
      const res = await fetch(`${API}/api/sln/portfolios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ portfolio_name: name.trim(), portfolio_code: code.trim() || undefined, description: desc.trim() || undefined }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg = typeof j.detail === 'string' ? j.detail : (Array.isArray(j.detail) ? j.detail.map(d => d.msg || JSON.stringify(d)).join(', ') : JSON.stringify(j.detail || 'Failed'));
        throw new Error(msg);
      }
      onCreated();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} data-testid="new-portfolio-modal">
      <div className="w-full max-w-md rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }} onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b" style={{ borderColor: t.cardBorder }}>
          <div className="text-[13px] font-bold">New portfolio</div>
          <div className="text-[11px]" style={{ color: t.textDim }}>A portfolio groups projects/assets together.</div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>NAME *</div>
            <input value={name} onChange={e => setName(e.target.value)} autoFocus
              className="w-full rounded px-2 py-1 text-[12px]"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="new-portfolio-name" />
          </div>
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>CODE (optional)</div>
            <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="auto"
              className="w-full rounded px-2 py-1 text-[12px] font-mono"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="new-portfolio-code" />
          </div>
          <div>
            <div className="text-[11px] font-semibold mb-1" style={{ color: t.textDim }}>DESCRIPTION</div>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
              className="w-full rounded px-2 py-1 text-[12px]"
              style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
              data-testid="new-portfolio-desc" />
          </div>
          {err && <div className="text-[11px]" style={{ color: '#f87171' }}>{err}</div>}
        </div>
        <div className="p-3 border-t flex gap-2 justify-end" style={{ borderColor: t.cardBorder }}>
          <button onClick={onClose} className="px-3 py-1 rounded text-[12px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
          <button onClick={save} disabled={busy}
            className="px-3 py-1 rounded text-[12px] font-semibold disabled:opacity-50"
            style={{ background: t.accent, color: '#000' }}
            data-testid="new-portfolio-save">
            {busy ? 'Creating…' : 'Create portfolio'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Sidebar item ── */
const SideItem = ({ icon: Icon, label, active, onClick, t, badge, chevron, expandedModule, moduleId, collapsed, testId }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-1.5 ${collapsed ? 'px-1 justify-center' : 'px-2'} py-1.5 rounded transition-colors`}
    style={{ background: active ? t.accentBg : 'transparent', color: active ? t.accent : t.textMuted }}
    title={collapsed ? label : undefined}
    data-testid={testId}
  >
    {!collapsed && chevron && (expandedModule === moduleId ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />)}
    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
    {!collapsed && <span className="truncate font-medium">{label}</span>}
    {!collapsed && badge && <span className="text-[7px] ml-auto px-1 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>{badge}</span>}
  </button>
);

/* ── Dashboard ── */
const ModuleBadge = ({ label, bg, color }) => (
  <span className="px-1.5 py-0.5 rounded font-bold tracking-wider" style={{ background: bg, color }}>{label}</span>
);

const Dashboard = ({ t, portfolios, moduleAccess, onRefresh, onOpenPortfolio, onOpenAssetModule }) => {
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      const allAssets = {};
      for (const p of portfolios) {
        try { allAssets[p.id] = await fetchSln(`/api/sln/portfolios/${p.id}/assets`); } catch { allAssets[p.id] = []; }
      }
      setAssets(allAssets);
    };
    load().finally(() => setLoading(false));
  }, [portfolios]);

  const totalStructures = Object.values(assets).flat().length;
  const totalElements = portfolios.reduce((s, p) => s + (p.element_count || 0), 0);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="prioris-dashboard">
      <div className="mb-6">
        <h2 className="text-sm font-bold" style={{ color: t.text }}>Prioris</h2>
        <p className="text-[12px]" style={{ color: t.textDim }}>Materials lifecycle management for the built environment</p>
      </div>

      {/* Portfolio summary */}
      <div className="p-4 rounded-lg mb-4" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-center gap-2 mb-2">
          <FolderTree className="w-3.5 h-3.5" style={{ color: t.accent }} />
          <span className="text-[12px] font-bold" style={{ color: t.text }}>Portfolio</span>
        </div>
        <div className="flex items-center gap-6">
          <div>
            <span className="text-2xl font-bold" style={{ color: t.text }}>{portfolios.length}</span>
            <span className="text-[11px] ml-1.5" style={{ color: t.textDim }}>{portfolios.length === 1 ? 'portfolio' : 'portfolios'}</span>
          </div>
          <div>
            <span className="text-2xl font-bold" style={{ color: t.text }}>{totalStructures}</span>
            <span className="text-[11px] ml-1.5" style={{ color: t.textDim }}>{totalStructures === 1 ? 'structure' : 'structures'}</span>
          </div>
          <div>
            <span className="text-2xl font-bold" style={{ color: t.text }}>{totalElements}</span>
            <span className="text-[11px] ml-1.5" style={{ color: t.textDim }}>elements</span>
          </div>
        </div>
      </div>

      {/* Per-portfolio asset grid (Service Life style) */}
      <div className="space-y-5 mt-4">
        {loading && <div className="p-4 text-[11px] text-center" style={{ color: t.textDim }}>Loading…</div>}
        {portfolios.map(p => {
          const pAssets = assets[p.id] || [];
          return (
            <div key={p.id}>
              <button onClick={() => onOpenPortfolio && onOpenPortfolio(p)}
                className="flex items-center gap-2 mb-2 px-1 w-full text-left hover:opacity-80 transition-opacity"
                data-testid={`dash-portfolio-${p.id}`}>
                <FolderTree className="w-3.5 h-3.5" style={{ color: t.accent }} />
                <div className="flex-1">
                  <div className="text-[12px] font-bold" style={{ color: t.text }}>{p.portfolio_name || p.name}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>
                    {pAssets.length} {pAssets.length === 1 ? 'asset' : 'assets'} · {p.element_count || 0} elements
                  </div>
                </div>
                <ChevronRight className="w-3 h-3" style={{ color: t.textDim }} />
              </button>
              {pAssets.length === 0 ? (
                <div className="p-4 rounded-lg text-[11px] text-center" style={{ background: t.card, border: `1px dashed ${t.cardBorder}`, color: t.textDim }}>
                  No assets yet under this portfolio.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pAssets.map(a => (
                    <AssetCard key={a.id} t={t} asset={a} moduleAccess={moduleAccess}
                      onOpenModule={(modKey) => onOpenAssetModule && onOpenAssetModule(a, modKey, p.portfolio_name || p.name)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── SL-style asset card with linkable module badges ── */
const AssetCard = ({ t, asset: a, moduleAccess, onOpenModule }) => {
  const status = a.worst_status || 'unknown';
  const statusStyle = ({
    act:     { bg: 'rgba(239,68,68,0.15)',  color: '#f87171', label: 'ACT NOW' },
    monitor: { bg: 'rgba(234,179,8,0.15)',  color: '#eab308', label: 'MONITOR' },
    ok:      { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: 'ADEQUATE' },
    unknown: { bg: 'rgba(148,163,184,0.1)', color: '#94a3b8', label: 'NOT ASSESSED' },
  })[status] || { bg: 'rgba(148,163,184,0.1)', color: '#94a3b8', label: 'NOT ASSESSED' };
  const handleBadge = (e, modKey) => { e.stopPropagation(); if (onOpenModule) onOpenModule(modKey); };
  return (
    <div className="p-3 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid={`dash-asset-${a.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <Building2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold leading-tight" style={{ color: t.text }}>{a.asset_name}</div>
          <div className="text-[10px]" style={{ color: t.textDim }}>{a.asset_type || '—'}{a.location ? ` — ${a.location}` : ''}</div>
        </div>
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider"
          style={{ background: statusStyle.bg, color: statusStyle.color }}>
          {statusStyle.label}
        </span>
      </div>
      <div className="flex gap-3 text-[10px]" style={{ color: t.textDim }}>
        <span>{a.zones_count ?? a.zone_count ?? 0} zones</span>
        <span>{a.elements_count ?? a.element_count ?? 0} elements</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[9px]">
        {moduleAccess?.materials_approval && (
          <button onClick={(e) => handleBadge(e, 'approval')} data-testid={`dash-asset-${a.id}-badge-ma`}
            className="px-1.5 py-0.5 rounded font-semibold tracking-wider transition-transform hover:scale-110"
            style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>MA</button>
        )}
        {moduleAccess?.quality_control && (
          <button onClick={(e) => handleBadge(e, 'qc')} data-testid={`dash-asset-${a.id}-badge-qc`}
            className="px-1.5 py-0.5 rounded font-semibold tracking-wider transition-transform hover:scale-110"
            style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>QC</button>
        )}
        {moduleAccess?.sensors_monitoring && (
          <button onClick={(e) => handleBadge(e, 'sensors')} data-testid={`dash-asset-${a.id}-badge-sensors`}
            className="px-1.5 py-0.5 rounded font-semibold tracking-wider transition-transform hover:scale-110"
            style={{ background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}>Sensors</button>
        )}
        {moduleAccess?.service_life && (
          <button onClick={(e) => handleBadge(e, 'service_life')} data-testid={`dash-asset-${a.id}-badge-sl`}
            className="px-1.5 py-0.5 rounded font-semibold tracking-wider transition-transform hover:scale-110"
            style={{ background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}>SL</button>
        )}
      </div>
    </div>
  );
};

/* ── Portfolio detail view (SL-style): breadcrumb + add asset + delete + asset grid ── */
const PortfolioDetailView = ({ t, portfolio, assets, moduleAccess, onBack, onOpenAsset, onOpenAssetModule, onDeletePortfolio, onDeleteAsset, onAssetsChanged }) => {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ asset_name: '', asset_code: '', asset_type: 'building', location: '', commissioning_year: '' });
  const [saving, setSaving] = useState(false);
  const assetTypes = ['building', 'bridge', 'tunnel', 'dam', 'marine', 'road', 'custom'];

  const submit = async () => {
    if (!form.asset_name.trim()) return;
    setSaving(true);
    try {
      await fetchSln(`/api/sln/portfolios/${portfolio.id}/assets`, {
        method: 'POST',
        body: JSON.stringify({
          asset_name: form.asset_name.trim(),
          asset_code: form.asset_code.trim() || undefined,
          asset_type: form.asset_type,
          location: form.location.trim(),
          commissioning_year: form.commissioning_year ? Number(form.commissioning_year) : undefined,
        }),
      });
      setCreating(false);
      setForm({ asset_name: '', asset_code: '', asset_type: 'building', location: '', commissioning_year: '' });
      if (onAssetsChanged) onAssetsChanged();
      window.dispatchEvent(new CustomEvent('prioris-portfolios-changed'));
    } catch (e) { alert(e.message || 'Create failed'); }
    setSaving(false);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="prioris-portfolio-detail">
      {/* Breadcrumb */}
      <div className="mb-3 text-[11px] flex items-center gap-1.5" style={{ color: t.textDim }}>
        <button onClick={onBack} className="hover:underline" style={{ color: t.accent }} data-testid="portfolio-detail-crumb-home">Prioris</button>
        <ChevronRight className="w-3 h-3" />
        <span style={{ color: t.text }}>{portfolio.portfolio_name || portfolio.name}</span>
      </div>

      {/* Header row */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold" style={{ color: t.text }}>{portfolio.portfolio_name || portfolio.name}</h2>
          <p className="text-[11px] mt-0.5" style={{ color: t.textDim }}>{portfolio.description || 'Portfolio'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCreating(c => !c)} data-testid="portfolio-detail-add-asset"
            className="px-3 py-1.5 rounded text-[11px] font-semibold flex items-center gap-1.5 transition-transform hover:scale-105"
            style={{ background: t.accent, color: '#fff' }}>
            <Plus className="w-3.5 h-3.5" /> Add Asset
          </button>
          <button onClick={onDeletePortfolio} data-testid="portfolio-detail-delete"
            className="p-1.5 rounded transition-colors hover:bg-red-500/20"
            title="Delete portfolio"
            style={{ color: '#ef4444', border: `1px solid rgba(239,68,68,0.35)` }}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Inline create form (SL-style) */}
      {creating && (
        <div className="mb-5 p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="portfolio-detail-new-asset-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Asset name *</div>
              <input value={form.asset_name} onChange={e => setForm({ ...form, asset_name: e.target.value })}
                placeholder="e.g. Sunny House 3"
                className="w-full rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
                autoFocus data-testid="portfolio-detail-asset-name" />
            </div>
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Asset code</div>
              <input value={form.asset_code} onChange={e => setForm({ ...form, asset_code: e.target.value })}
                placeholder="auto"
                className="w-full rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Type</div>
              <select value={form.asset_type} onChange={e => setForm({ ...form, asset_type: e.target.value })}
                className="w-full rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}>
                {assetTypes.map(at => <option key={at} value={at}>{at}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Location</div>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Hong Kong"
                className="w-full rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
            <div>
              <div className="text-[10px] font-semibold mb-0.5" style={{ color: t.textDim }}>Commissioning year</div>
              <input type="number" value={form.commissioning_year} onChange={e => setForm({ ...form, commissioning_year: e.target.value })}
                placeholder="e.g. 2005"
                className="w-full rounded px-2 py-1 text-[11px]"
                style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setCreating(false)} className="px-3 py-1 rounded text-[11px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
            <button onClick={submit} disabled={saving || !form.asset_name.trim()}
              className="px-3 py-1 rounded text-[11px] font-semibold"
              style={{ background: t.accent, color: '#fff', opacity: (saving || !form.asset_name.trim()) ? 0.5 : 1 }}
              data-testid="portfolio-detail-asset-save">
              {saving ? 'Saving…' : 'Create asset'}
            </button>
          </div>
        </div>
      )}

      {/* Assets grid */}
      {assets.length === 0 ? (
        <div className="p-8 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
          <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
          <p className="text-[12px]" style={{ color: t.textDim }}>No assets yet. Click <strong>+ Add Asset</strong> to create your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {assets.map(a => (
            <div key={a.id} className="group relative">
              <div onClick={() => onOpenAsset(a)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenAsset(a); } }}
                className="block w-full text-left cursor-pointer"
                data-testid={`portfolio-detail-asset-${a.id}`}>
                <AssetCard t={t} asset={a} moduleAccess={moduleAccess}
                  onOpenModule={(modKey) => onOpenAssetModule(a, modKey)} />
              </div>
              <button onClick={(e) => { e.stopPropagation(); onDeleteAsset(a.id); }}
                className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                title="Delete asset"
                style={{ color: '#ef4444' }}
                data-testid={`portfolio-detail-asset-delete-${a.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Project Overview (when project selected but no module) ── */
const ProjectOverview = ({ t, project, portfolio, moduleAccess, onSelectModule, onExpandModule, onDelete }) => {
  const [slData, setSlData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      // Load zones + elements for this asset for SL metrics
      try {
        const zones = await fetchSln(`/api/sln/assets/${project.id}/zones`);
        let elements = [];
        for (const z of zones) {
          try {
            const elems = await fetchSln(`/api/sln/zones/${z.id}/elements`);
            elements.push(...elems);
          } catch {}
        }
        setSlData({ zones, elements });
      } catch { setSlData({ zones: [], elements: [] }); }
    };
    load().finally(() => setLoading(false));
  }, [project.id]);

  const zones = slData?.zones || [];
  const elements = slData?.elements || [];
  const actNow = elements.filter(e => e.status === 'act').length;
  const monitor = elements.filter(e => e.status === 'monitor').length;
  const adequate = elements.filter(e => e.status === 'ok' || e.status === 'adequate').length;
  const unknown = elements.filter(e => !e.status || e.status === 'unknown').length;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-1 text-[11px]" style={{ color: t.textDim }}>{portfolio}</div>
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-sm font-bold" style={{ color: t.text }}>{project.asset_name || project.name || 'Project'}</h2>
        {onDelete && (
          <button onClick={onDelete} data-testid="project-overview-delete"
            className="p-1.5 rounded transition-colors hover:bg-red-500/20"
            title="Delete asset"
            style={{ color: '#ef4444', border: `1px solid rgba(239,68,68,0.35)` }}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Module cards row */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        {moduleAccess?.materials_approval && (
          <button onClick={() => { onSelectModule('approval'); onExpandModule('approval'); }} className="text-left p-4 rounded-lg border transition-colors hover:brightness-110 group" style={{ background: t.card, borderColor: t.cardBorder }}>
            <FileCheck className="w-4 h-4 mb-2" style={{ color: t.accent }} />
            <h3 className="text-xs font-bold" style={{ color: t.text }}>Materials Approval</h3>
            <p className="text-[11px] mt-1" style={{ color: t.textDim }}>Approve mixes and trial data</p>
          </button>
        )}
        {moduleAccess?.quality_control && (
          <button onClick={() => { onSelectModule('qc'); onExpandModule('qc'); }} data-testid="asset-nav-quality-control" className="text-left p-4 rounded-lg border transition-colors hover:brightness-110 group" style={{ background: t.card, borderColor: t.cardBorder }}>
            <FlaskConical className="w-4 h-4 mb-2" style={{ color: t.accent }} />
            <h3 className="text-xs font-bold" style={{ color: t.text }}>Quality Control</h3>
            <p className="text-[11px] mt-1" style={{ color: t.textDim }}>Identity testing &amp; conformity</p>
          </button>
        )}
        {moduleAccess?.service_life && (
          <button onClick={() => onSelectModule('service_life')} className="text-left p-4 rounded-lg border transition-colors hover:brightness-110 group" style={{ background: t.card, borderColor: t.cardBorder }}>
            <Microscope className="w-4 h-4 mb-2" style={{ color: '#6366f1' }} />
            <h3 className="text-xs font-bold" style={{ color: t.text }}>Service Life</h3>
            <p className="text-[11px] mt-1" style={{ color: t.textDim }}>Residual life forecasting</p>
          </button>
        )}
      </div>

      {/* Detailed module panels below */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* MA Detail */}
        {moduleAccess?.materials_approval && (
          <div className="p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <div className="flex items-center gap-2 mb-3">
              <FileCheck className="w-3 h-3" style={{ color: t.accent }} />
              <span className="text-[11px] font-bold" style={{ color: t.text }}>Approval Status</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span style={{ color: t.textDim }}>Concrete mixes</span>
                <span className="flex items-center gap-1" style={{ color: t.textDim }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} /> Pending
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span style={{ color: t.textDim }}>Steel (rebar)</span>
                <span style={{ color: t.textDim }}>—</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span style={{ color: t.textDim }}>Aggregates</span>
                <span style={{ color: t.textDim }}>—</span>
              </div>
              <div className="h-px mt-2" style={{ background: t.cardBorder }} />
              <div className="flex items-center gap-3 pt-1 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10b981' }} /> 0 approved</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} /> 0 in review</span>
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#ef4444' }} /> 0 rejected</span>
              </div>
            </div>
          </div>
        )}

        {/* QC Detail */}
        {moduleAccess?.quality_control && (
          <div className="p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <div className="flex items-center gap-2 mb-3">
              <FlaskConical className="w-3 h-3" style={{ color: t.accent }} />
              <span className="text-[11px] font-bold" style={{ color: t.text }}>QC Summary</span>
            </div>
            <div className="space-y-2">
              {[
                { mat: 'Concrete', status: 'active', color: '#10b981' },
                { mat: 'Steel', status: 'not started', color: t.textDim },
                { mat: 'Asphalt', status: 'not started', color: t.textDim },
                { mat: 'Earthworks', status: 'not started', color: t.textDim },
              ].map((m, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span style={{ color: t.textMuted }}>{m.mat}</span>
                  <span className="flex items-center gap-1" style={{ color: m.color }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
                    {m.status}
                  </span>
                </div>
              ))}
              <div className="h-px mt-2" style={{ background: t.cardBorder }} />
              <p className="text-[10px] pt-1" style={{ color: '#10b981' }}>No active early warnings</p>
            </div>
          </div>
        )}

        {/* SL Detail */}
        {moduleAccess?.service_life && (
          <div className="p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
            <div className="flex items-center gap-2 mb-3">
              <Microscope className="w-3 h-3" style={{ color: '#6366f1' }} />
              <span className="text-[11px] font-bold" style={{ color: t.text }}>Durability Status</span>
            </div>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: t.textDim }} />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px]">
                  <span style={{ color: t.textDim }}>Zones</span>
                  <span style={{ color: t.text }}><strong>{zones.length}</strong></span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span style={{ color: t.textDim }}>Elements</span>
                  <span style={{ color: t.text }}><strong>{elements.length}</strong></span>
                </div>
                <div className="h-px mt-2" style={{ background: t.cardBorder }} />
                <div className="flex items-center gap-3 pt-1 text-[10px]">
                  {actNow > 0 && <span className="flex items-center gap-1" style={{ color: '#ef4444' }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#ef4444' }} /><strong>{actNow}</strong> act now</span>}
                  {monitor > 0 && <span className="flex items-center gap-1" style={{ color: '#f59e0b' }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} /><strong>{monitor}</strong> monitor</span>}
                  {adequate > 0 && <span className="flex items-center gap-1" style={{ color: '#10b981' }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10b981' }} />{adequate} adequate</span>}
                  {unknown > 0 && <span className="flex items-center gap-1" style={{ color: t.textDim }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: t.textDim }} />{unknown} pending</span>}
                  {elements.length === 0 && <span style={{ color: t.textDim }}>No elements assessed yet</span>}
                </div>
                {project.beta_min != null && (
                  <div className="text-[10px] pt-1" style={{ color: project.beta_min < 0 ? '#ef4444' : project.beta_min < 1.5 ? '#f59e0b' : t.textDim }}>
                    &beta;<sub>min</sub> = {project.beta_min}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── MA Portfolio Dashboard ── */
const MAPortfolioDashboard = ({ t, onSelectProject }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [assets, setAssets] = useState({});
  const [summaries, setSummaries] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSln('/api/sln/portfolios').then(async (data) => {
      setPortfolios(data);
      const allAssets = {};
      const sums = {};
      for (const p of data) {
        try { allAssets[p.id] = await fetchSln(`/api/sln/portfolios/${p.id}/assets`); } catch { allAssets[p.id] = []; }
        for (const a of (allAssets[p.id] || [])) {
          try { sums[a.id] = await fetchSln(`/api/prioris/ma/summary?project_id=${a.id}`); } catch { sums[a.id] = null; }
        }
      }
      setAssets(allAssets);
      setSummaries(sums);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;

  const totalAssets = Object.values(assets).flat().length;
  const totalSubmittals = Object.values(summaries).reduce((a, s) => a + (s?.total || 0), 0);

  return (
    <div className="px-6 py-5" data-testid="prioris-ma-dashboard">
      <div className="text-xs mb-1" style={{ color: t.accent }}>Materials Approval</div>
      <h2 className="text-lg font-bold mb-1" style={{ color: t.text }}>Approval Dashboard</h2>
      <p className="text-[12px] mb-6" style={{ color: t.textDim }}>Submittal status across all projects</p>

      <div className="flex items-center gap-6 text-[12px] mb-6" style={{ color: t.textDim }}>
        <span>{portfolios.length} portfolios</span>
        <span>{totalAssets} projects</span>
        <span>{totalSubmittals} submittals</span>
      </div>

      {portfolios.map(p => (
        <div key={p.id} className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FolderTree className="w-3.5 h-3.5" style={{ color: t.accent }} />
            <h3 className="text-xs font-bold" style={{ color: t.text }}>{p.portfolio_name}</h3>
            <span className="text-[11px]" style={{ color: t.textDim }}>{(assets[p.id] || []).length} projects</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(assets[p.id] || []).map(asset => {
              const s = summaries[asset.id] || { total: 0, by_status: {} };
              const bs = s.by_status || {};
              const approved = bs['Approved'] || 0;
              const review = bs['Under Review'] || 0;
              const action = bs['Action Required'] || 0;
              const rejected = bs['Rejected'] || 0;
              const draft = bs['Draft'] || 0;
              const overallStatus = s.total === 0 ? 'empty' : (rejected + action) > 0 ? 'rejected' : review > 0 ? 'review' : approved > 0 ? 'approved' : 'draft';
              const statusMeta = {
                approved: { label: 'ALL APPROVED', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
                review:   { label: 'UNDER REVIEW', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
                rejected: { label: 'ACTION REQ.',  color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
                draft:    { label: 'DRAFT',        color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
                empty:    { label: 'NO SUBMITTALS',color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' },
              };
              const sm = statusMeta[overallStatus];
              return (
                <button key={asset.id}
                  onClick={() => onSelectProject && onSelectProject(asset)}
                  className="text-left p-4 rounded-lg transition-colors hover:opacity-90"
                  style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}
                  data-testid={`prioris-ma-asset-${asset.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-[12px] font-bold" style={{ color: t.text }}>{asset.asset_name}</h4>
                      <p className="text-[10px]" style={{ color: t.textDim }}>{asset.asset_type} — {asset.location}</p>
                    </div>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] mb-2" style={{ color: t.textDim }}>
                    <span>{s.total} submittals</span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10b981' }} /><span className="text-[10px]" style={{ color: t.textDim }}>{approved}</span></div>
                    <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#f59e0b' }} /><span className="text-[10px]" style={{ color: t.textDim }}>{review}</span></div>
                    <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#3b82f6' }} /><span className="text-[10px]" style={{ color: t.textDim }}>{draft}</span></div>
                    {(rejected + action) > 0 && <div className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: '#ef4444' }} /><span className="text-[10px]" style={{ color: t.textDim }}>{rejected + action}</span></div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {portfolios.length === 0 && (
        <div className="p-8 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
          <FileCheck className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
          <p className="text-[12px]" style={{ color: t.textDim }}>No portfolios created yet.</p>
        </div>
      )}
    </div>
  );
};

/* ── MA Asset Dashboard (when a specific project is selected) ── */
const MANoSpecsPrompt = ({ t, onOpenLib }) => (
  <div className="p-10 max-w-2xl mx-auto text-center" data-testid="ma-no-specs-prompt">
    <ShieldCheck className="w-12 h-12 mx-auto mb-3" style={{ color: t.accent }} />
    <h3 className="text-lg font-bold mb-2" style={{ color: t.text }}>Select project specs first</h3>
    <p className="text-[12px] mb-4" style={{ color: t.textMuted }}>
      Materials Approval is spec-driven. Open the <strong>Spec Library</strong>, pick the applicable clauses (or apply an entire volume like 14.03.6 Betão), then come back to add submittals tied to those clauses.
    </p>
    <button onClick={onOpenLib} className="px-3 py-1.5 rounded text-[12px] font-semibold inline-flex items-center gap-1.5"
      style={{ background: t.accent, color: '#000' }} data-testid="ma-open-speclib-cta">
      <ShieldCheck className="w-3 h-3" /> Open Spec Library
    </button>
  </div>
);

const MAAssetDashboard = ({ t, project, specSelections, onPickRubrica }) => {
  const [summary, setSummary] = useState(null);
  // Track which document groups are expanded on the landing page.
  // Default = ALL COLLAPSED so the page leads with the section roll-ups and
  // the table detail drills down only when the user explicitly opens it.
  const [openDocs, setOpenDocs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dd-prioris-ma-dash-docs-open') || '{}'); }
    catch { return {}; }
  });
  const toggleDoc = (k) => setOpenDocs(prev => {
    const next = { ...prev, [k]: !prev[k] };
    try { localStorage.setItem('dd-prioris-ma-dash-docs-open', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    if (!project?.id) { setSummary(null); return; }
    fetchSln(`/api/prioris/ma/summary?project_id=${project.id}`)
      .then(setSummary).catch(() => setSummary(null));
  }, [project?.id]);

  // Group spec selections by document (section-level rollup)
  const byDoc = {};
  specSelections.forEach(sel => {
    const docKey = sel.table_key.split('-').slice(0, 2).join('-'); // e.g. HKCEDD-16, FP24-552
    (byDoc[docKey] = byDoc[docKey] || []).push(sel);
  });

  const docLabel = (docKey) => {
    if (docKey.startsWith('HKCEDD-16')) return 'Concrete · Section 16 (HK CEDD)';
    if (docKey.startsWith('HKCEDD-21')) return 'Marine concrete · Section 21 (HK CEDD)';
    if (docKey.startsWith('HKCEDD-CS3')) return 'CS3 · Aggregates (HK CEDD)';
    if (docKey.startsWith('FP24-552')) return 'Structural concrete · Div 500 §552 (FHWA FP-24)';
    if (docKey.startsWith('FP24-701')) return 'Cementitious · §701 (FHWA FP-24)';
    if (docKey.startsWith('FP24-703')) return 'Aggregates · §703 (FHWA FP-24)';
    if (docKey.startsWith('FP24-709')) return 'Reinforcing steel · §709 (FHWA FP-24)';
    if (docKey.startsWith('MCHW-17')) return 'Structural concrete · Series 1700 (UK MCHW)';
    if (docKey.startsWith('14.03')) return 'Pavements · 14.03 (PT EP)';
    return docKey;
  };

  const StatusPill = ({ label, count, color }) => (
    <div className="px-2 py-1 rounded text-[11px] flex items-center gap-1.5"
      style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}
      data-testid={`ma-dash-status-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="font-bold">{count}</span>
      <span style={{ color: t.textMuted }}>{label}</span>
    </div>
  );

  const bs = summary?.by_status || {};

  return (
    <div className="p-4" data-testid="ma-asset-dashboard">
      {/* Top summary card */}
      <div className="mb-3 p-3 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: t.accent }}>PROJECT SPECS · {project?.asset_name || project?.name}</div>
            <div className="text-[11px] mt-1" style={{ color: t.textMuted }}>
              {specSelections.length} spec table(s) applied · pick a clause below to manage submittals.
            </div>
          </div>
          {summary && (
            <div className="flex gap-1.5 flex-wrap" data-testid="ma-dash-status-row">
              <StatusPill label="Total"           count={summary.total || 0}          color="#a78bfa" />
              <StatusPill label="Draft"           count={bs.Draft || 0}               color="#94a3b8" />
              <StatusPill label="Under Review"    count={bs['Under Review'] || 0}     color="#60a5fa" />
              <StatusPill label="Action Required" count={bs['Action Required'] || 0}  color="#f59e0b" />
              <StatusPill label="Approved"        count={bs.Approved || 0}            color="#22c55e" />
              <StatusPill label="Rejected"        count={bs.Rejected || 0}            color="#ef4444" />
            </div>
          )}
        </div>
        {summary && (
          <div className="mt-2 flex gap-4 flex-wrap text-[11px]" style={{ color: t.textMuted }}>
            <span data-testid="ma-dash-avg-turnaround">
              Avg approval turnaround: <strong style={{ color: t.text }}>{summary.avg_turnaround_days == null ? '—' : `${summary.avg_turnaround_days} d`}</strong>
            </span>
            <span data-testid="ma-dash-oldest-open">
              Oldest open submittal: <strong style={{ color: summary.oldest_open_days > 14 ? '#ef4444' : t.text }}>{summary.oldest_open_days ? `${summary.oldest_open_days} d` : '—'}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Per-section rollup cards */}
      {summary?.sections && summary.sections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="ma-section-rollup">
          {summary.sections.map(sec => (
            <div key={sec.section_key} className="p-2.5 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}
              data-testid={`ma-section-card-${sec.section_key}`}>
              <div className="text-[11px] font-mono" style={{ color: t.accent }}>{sec.section_key}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold" style={{ color: t.text }}>{sec.total}</span>
                <span className="text-[11px]" style={{ color: t.textMuted }}>submittals · {sec.active_clauses} clause(s) active</span>
              </div>
              <div className="mt-1.5 flex gap-1 flex-wrap text-[10px]">
                {Object.entries(sec.by_status).filter(([, n]) => n > 0).map(([s, n]) => (
                  <span key={s} className="px-1.5 py-0.5 rounded" style={{ background: t.inputBg, color: t.textMuted, border: `1px solid ${t.inputBorder}` }}>
                    <strong style={{ color: t.text }}>{n}</strong> {s}
                  </span>
                ))}
              </div>
              <div className="mt-1.5 text-[10px] flex gap-3" style={{ color: t.textMuted }}>
                <span>Avg: <strong style={{ color: t.text }}>{sec.avg_turnaround_days == null ? '—' : `${sec.avg_turnaround_days} d`}</strong></span>
                <span>Oldest open: <strong style={{ color: sec.oldest_open_days > 14 ? '#ef4444' : t.text }}>{sec.oldest_open_days ? `${sec.oldest_open_days} d` : '—'}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Grouped spec tables (by document) — collapsed by default; click header to drill down */}
      {Object.entries(byDoc).map(([docKey, sels]) => {
        const isOpen = !!openDocs[docKey];
        const totalClauses = sels.reduce((s, x) => s + (x.rubrica_details?.length || 0), 0);
        return (
          <div key={docKey} className="mb-2">
            <button
              onClick={() => toggleDoc(docKey)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:opacity-80"
              style={{ background: isOpen ? t.accentBg : t.card, border: `1px solid ${isOpen ? t.accent : t.cardBorder}` }}
              data-testid={`ma-doc-toggle-${docKey}`}>
              <ChevronRight
                className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
                style={{
                  transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                  color: isOpen ? t.accent : t.textMuted,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wider truncate" style={{ color: isOpen ? t.accent : t.textMuted }}>
                  {docLabel(docKey)}
                </div>
                <div className="text-[10px]" style={{ color: t.textDim }}>
                  {sels.length} table{sels.length === 1 ? '' : 's'} · {totalClauses} clause{totalClauses === 1 ? '' : 's'}
                </div>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                style={{ background: t.inputBg, color: t.textDim, border: `1px solid ${t.inputBorder}` }}>
                {isOpen ? 'Hide' : 'Show'}
              </span>
            </button>
            {isOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {sels.map(sel => (
                  <div key={sel.table_key} className="p-3 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
                    <div className="text-[11px] font-mono mb-1" style={{ color: t.accent }}>{sel.table_key} · {sel.standard}</div>
                    <div className="text-[12px] font-bold mb-2" style={{ color: t.text }}>{sel.table_label}</div>
                    <div className="space-y-1">
                      {(sel.rubrica_details || []).map(r => (
                        <button key={r.code} onClick={() => onPickRubrica(sel, r)}
                          className="w-full text-left px-2 py-1 rounded text-[11px] flex items-center gap-2 hover:-translate-y-0.5 transition-transform"
                          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}` }}
                          data-testid={`ma-dash-rubrica-${r.code}`}>
                          <span className="font-mono" style={{ color: t.accent }}>{r.code}</span>
                          <span className="flex-1 truncate" style={{ color: t.text }}>{r.label}</span>
                          <ChevronRight className="w-3 h-3" style={{ color: t.textMuted }} />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── QC Portfolio Dashboard ── */
const QCPortfolioDashboard = ({ t, onDrillAsset }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSln('/api/sln/portfolios').then(async (data) => {
      setPortfolios(data);
      const allAssets = {};
      for (const p of data) {
        try { allAssets[p.id] = await fetchSln(`/api/sln/portfolios/${p.id}/assets`); } catch { allAssets[p.id] = []; }
      }
      setAssets(allAssets);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: t.accent }} /></div>;

  const totalAssets = Object.values(assets).flat().length;

  return (
    <div className="px-6 py-5" data-testid="prioris-qc-dashboard">
      <div className="text-xs mb-1" style={{ color: t.accent }}>Quality Control</div>
      <h2 className="text-lg font-bold mb-1" style={{ color: t.text }}>QC Dashboard</h2>
      <p className="text-[12px] mb-6" style={{ color: t.textDim }}>Conformity and production control across all projects</p>

      <div className="flex items-center gap-6 text-[12px] mb-6" style={{ color: t.textDim }}>
        <span>{portfolios.length} portfolios</span>
        <span>{totalAssets} projects</span>
      </div>

      {portfolios.map(p => (
        <div key={p.id} className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <FolderTree className="w-3.5 h-3.5" style={{ color: t.accent }} />
            <h3 className="text-xs font-bold" style={{ color: t.text }}>{p.portfolio_name}</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(assets[p.id] || []).map(asset => (
              <button key={asset.id} onClick={() => onDrillAsset(asset)}
                className="text-left p-4 rounded-lg transition-all hover:brightness-110 group"
                style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-[12px] font-bold group-hover:underline" style={{ color: t.text }}>{asset.asset_name}</h4>
                    <p className="text-[10px]" style={{ color: t.textDim }}>{asset.asset_type} — {asset.location}</p>
                  </div>
                  <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: t.textMuted }} />
                </div>
                <div className="flex items-center gap-3 text-[10px]" style={{ color: t.textDim }}>
                  <span>{asset.zone_count || 0} zones</span>
                  <span>{asset.element_count || 0} elements</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      {portfolios.length === 0 && (
        <div className="p-8 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
          <FlaskConical className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
          <p className="text-[12px]" style={{ color: t.textDim }}>No portfolios created yet.</p>
        </div>
      )}
    </div>
  );
};

/* ── QC Asset Material Dashboard (click an asset → see material types) ── */
const QC_MATERIAL_TYPES = [
  { id: 'ITP', icon: ClipboardList, color: '#a855f7', desc: 'Inspection & Test Plan · Pouring Records, slump/temp/air tests, site documentation' },
  { id: 'Concrete', icon: FlaskConical, color: '#10b981', desc: 'Identity testing, conformity checks, early warning projection' },
  { id: 'Steel', icon: Building2, color: '#3b82f6', desc: 'Rebar and structural steel testing' },
  { id: 'Asphalt', icon: Layers, color: '#f59e0b', desc: 'Mix design verification and compaction testing' },
  { id: 'Earthworks', icon: FolderTree, color: '#8b5cf6', desc: 'Compaction, CBR, and moisture content' },
];

const QCAssetMaterialDashboard = ({ t, asset, onDrillMaterial, onBack }) => (
  <div className="px-6 py-5" data-testid="prioris-qc-asset-materials">
    <button onClick={onBack} className="flex items-center gap-1 text-[11px] mb-3 hover:underline" style={{ color: t.accent }}>
      <ChevronRight className="w-3 h-3 rotate-180" /> Back to QC Dashboard
    </button>
    <div className="text-xs mb-1" style={{ color: t.accent }}>Quality Control</div>
    <h2 className="text-lg font-bold mb-1" style={{ color: t.text }}>{asset.asset_name}</h2>
    <p className="text-[12px] mb-6" style={{ color: t.textDim }}>{asset.asset_type} — {asset.location} &middot; Select a material type</p>

    <div className="grid sm:grid-cols-2 gap-4">
      {QC_MATERIAL_TYPES.map(mat => {
        const isLive = mat.id === 'Concrete' || mat.id === 'ITP';
        return (
          <button key={mat.id} onClick={() => isLive && onDrillMaterial(mat.id)}
            className={`text-left p-5 rounded-lg transition-all group ${isLive ? 'hover:brightness-110 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}
            disabled={!isLive}
            data-testid={`qc-mat-${mat.id}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${mat.color}15` }}>
                <mat.icon className="w-4 h-4" style={{ color: mat.color }} />
              </div>
              <div className="flex-1">
                <h3 className="text-xs font-bold" style={{ color: t.text }}>{mat.id}</h3>
                {!isLive && <span className="text-[9px] font-bold uppercase" style={{ color: t.textDim }}>Coming soon</span>}
              </div>
              {isLive && <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100" style={{ color: t.textMuted }} />}
            </div>
            <p className="text-[11px]" style={{ color: t.textDim }}>{mat.desc}</p>
          </button>
        );
      })}
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════════
   QC Spec-driven Hub — shown when the user enters Quality Control for an
   asset that already has one or more ITPs applied via the Spec Library.
   Mirrors the MA asset dashboard, but grouped by ITP and control point.
   ══════════════════════════════════════════════════════════════════════════ */
const QC_CP_KIND_META = {
  pouring_record:       { icon: ClipboardList, color: '#10b981', badge: 'LIVE · Pouring records' },
  strength_statistical: { icon: FlaskConical,  color: '#10b981', badge: 'LIVE · Strength analysis' },
  readonly:             { icon: FileText,      color: '#94a3b8', badge: 'Read-only · Data capture soon' },
};

const QCSpecHub = ({ t, asset, applicableItps, onPickCheckpoint, onOpenSpecLib }) => {
  if (!applicableItps || applicableItps.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="qc-spec-hub-empty">
        <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: t.accent }}>Quality Control</div>
        <h2 className="text-lg font-bold mb-1" style={{ color: t.text }}>{asset.asset_name}</h2>
        <p className="text-[11px] mb-5" style={{ color: t.textDim }}>Control points for this asset will appear once you apply a spec table.</p>
        <div className="p-6 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" style={{ color: t.textDim }} />
          <p className="text-[12px] mb-3" style={{ color: t.textDim }}>
            No ITP applied yet. Go to <strong style={{ color: t.text }}>Spec Library</strong>, pick a jurisdiction (e.g. FHWA FP-24) and apply the relevant tables to this project.
          </p>
          <button onClick={onOpenSpecLib} className="px-4 py-2 rounded text-[11px] font-semibold"
            style={{ background: t.accent, color: '#fff' }} data-testid="qc-spec-hub-open-lib">
            Open Spec Library
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="qc-spec-hub">
      <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: t.accent }}>Quality Control</div>
      <h2 className="text-lg font-bold mb-1" style={{ color: t.text }}>{asset.asset_name}</h2>
      <p className="text-[11px] mb-5" style={{ color: t.textDim }}>
        Inspection & test plans driven by this project's spec selections. Click a control point to open its testing activity.
      </p>
      <div className="space-y-5">
        {applicableItps.map(itp => (
          <div key={itp.code} data-testid={`qc-itp-${itp.code}`}>
            <div className="flex items-baseline gap-2 mb-2 px-1">
              <ClipboardList className="w-3.5 h-3.5" style={{ color: t.accent }} />
              <div>
                <div className="text-[12px] font-bold" style={{ color: t.text }}>{itp.label_short || itp.code}</div>
                <div className="text-[10px]" style={{ color: t.textDim }}>{itp.standard}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(itp.control_points || []).map(cp => {
                const meta = QC_CP_KIND_META[cp.kind] || QC_CP_KIND_META.readonly;
                const Icon = meta.icon;
                return (
                  <button key={cp.code} onClick={() => onPickCheckpoint(itp.code, cp)}
                    className="text-left p-3 rounded-lg transition-all hover:brightness-110 group"
                    style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}
                    data-testid={`qc-hub-cp-${cp.code}`}>
                    <div className="flex items-start gap-2 mb-1">
                      <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}15` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className="font-mono text-[10px]" style={{ color: t.accent }}>{cp.code}</span>
                          <span className="text-[11px] font-semibold" style={{ color: t.text }}>{cp.label}</span>
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: t.textDim }}>{cp.description}</div>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 flex-shrink-0" style={{ color: t.textMuted }} />
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider uppercase"
                        style={{ background: `${meta.color}22`, color: meta.color }}>
                        {meta.badge}
                      </span>
                      {(cp.spec_refs || []).slice(0, 4).map(r => (
                        <span key={r} className="px-1 py-0 rounded text-[9px] font-mono"
                          style={{ background: t.inputBg, color: t.textMuted, border: `1px solid ${t.cardBorder}` }}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   QC Checkpoint read-only view — shown for `readonly` kind control points.
   Displays acceptance criteria from the matching ITP rows + "Data capture
   coming soon" placeholder. Users can see what tests and evidence are
   required; the actual data-entry form is a future phase.
   ══════════════════════════════════════════════════════════════════════════ */
const PriorisQCCheckpointView = ({ t, itpKey, cp, onBack }) => {
  const [itp, setItp] = useState(null);
  useEffect(() => {
    fetchSln(`/api/prioris/qc/itp/catalog/${itpKey}`)
      .then(setItp).catch(() => setItp(null));
  }, [itpKey]);

  const rows = useMemo(() => {
    if (!itp) return [];
    const codes = new Set(cp.row_codes || []);
    const out = [];
    for (const ph of itp.phases || []) {
      for (const r of ph.rows || []) {
        if (codes.has(r.code)) out.push({ ...r, phase_label: ph.label });
      }
    }
    return out;
  }, [itp, cp.row_codes]);

  return (
    <div className="p-6 max-w-5xl mx-auto text-[12px]" data-testid="qc-checkpoint-view">
      <button onClick={onBack} className="text-[11px] mb-2 flex items-center gap-1"
        style={{ color: t.accent }} data-testid="qc-checkpoint-back">
        <ChevronRight className="w-3 h-3 rotate-180" /> Back to QC control points
      </button>
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-[10px]" style={{ color: t.accent }}>{cp.code}</span>
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: t.textDim }}>
            {itp?.label_short || itpKey}
          </span>
        </div>
        <h2 className="text-lg font-bold" style={{ color: t.text }}>{cp.label}</h2>
        <p className="text-[11px] mt-1" style={{ color: t.textDim }}>{cp.description}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {(cp.spec_refs || []).map(r => (
            <span key={r} className="px-1.5 py-0.5 rounded text-[10px] font-mono"
              style={{ background: t.inputBg, color: t.textMuted, border: `1px solid ${t.cardBorder}` }}>
              {r}
            </span>
          ))}
        </div>
      </div>

      {/* Coming-soon banner */}
      <div className="mb-4 p-3 rounded flex items-start gap-2"
        style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.35)' }}
        data-testid="qc-checkpoint-coming-soon">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#eab308' }} />
        <div className="text-[11px]" style={{ color: t.text }}>
          <strong>Data capture coming soon.</strong>
          <span style={{ color: t.textMuted }}> Supplier evidence upload, sample form builder and automatic V/NV evaluation for this checkpoint are on the roadmap. For now, use this page as the field reference.</span>
        </div>
      </div>

      {/* Acceptance criteria table */}
      <div className="rounded overflow-hidden" style={{ border: `1px solid ${t.cardBorder}` }}>
        <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: t.inputBg, color: t.textMuted }}>
              {['Ref', 'Title', 'Method', 'Frequency', 'Report time', 'Split sample', 'Acceptance'].map(h => (
                <th key={h} className="text-left px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-4 text-center" style={{ background: t.card, color: t.textDim }}>
                Loading acceptance criteria…
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.code} style={{ background: t.card, color: t.text }}>
                <td className="px-2 py-1.5 font-mono text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.accent }}>{r.code}</td>
                <td className="px-2 py-1.5" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                  <div className="font-semibold">{r.title}</div>
                  <div className="text-[10px]" style={{ color: t.textDim }}>{r.point_of_sampling}</div>
                </td>
                <td className="px-2 py-1.5 text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>{r.test_method}</td>
                <td className="px-2 py-1.5 text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>{r.frequency}</td>
                <td className="px-2 py-1.5 text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>{r.report_time}</td>
                <td className="px-2 py-1.5 text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>{r.split_sample}</td>
                <td className="px-2 py-1.5 text-[10px]" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.text }}>{r.acceptance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[10px]" style={{ color: t.textDim }}>
        Reference clauses: {(cp.spec_refs || []).join(' · ')}. Pulled from <span className="font-mono">{itpKey}</span> — the ITP catalog activated by your Spec Library selection.
      </p>
    </div>
  );
};

/* ── Sensors Placeholder ── */
const SensorsPlaceholder = ({ t }) => (
  <div className="p-6 max-w-4xl mx-auto" data-testid="prioris-sensors-placeholder">
    <div className="flex items-center gap-2 mb-1">
      <h2 className="text-sm font-bold" style={{ color: t.text }}>Sensors & Monitoring</h2>
      <span className="text-[9px] font-bold tracking-wider uppercase px-1 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>beta</span>
    </div>
    <p className="text-[11px] mb-6" style={{ color: t.textDim }}>Real-time sensor data ingestion and threshold alerting.</p>
    <div className="grid sm:grid-cols-2 gap-3">
      {['Overview', 'Integrations'].map((tab, i) => (
        <div key={i} className="p-6 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
          <p className="text-[12px] font-semibold" style={{ color: t.textMuted }}>{tab}</p>
          <p className="text-[11px] mt-1" style={{ color: t.textDim }}>Under development</p>
        </div>
      ))}
    </div>
  </div>
);

/* ── Generic Placeholder ── */
const GenericPlaceholder = ({ label, t }) => (
  <div className="p-6 max-w-4xl mx-auto">
    <h2 className="text-sm font-bold mb-4" style={{ color: t.text }}>{label}</h2>
    <div className="p-12 rounded-lg border-2 border-dashed text-center" style={{ borderColor: t.cardBorder }}>
      <p className="text-[12px]" style={{ color: t.textDim }}>Coming soon</p>    </div>
  </div>
);

/* ── Prioris Settings view — theme + font-size slider ── */
const PriorisSettingsView = ({ t, theme, setTheme, fontScale, setFontScale }) => {
  const pct = Math.round(fontScale * 100);
  const swatch = (key, label) => (
    <button
      key={key}
      onClick={() => setTheme(key)}
      className="px-4 py-3 rounded-lg text-left flex-1"
      style={{
        background: theme === key ? t.accentBg : t.card,
        border: `2px solid ${theme === key ? t.accent : t.cardBorder}`,
        color: t.text,
      }}
      data-testid={`prioris-settings-theme-${key}`}>
      <div className="text-[13px] font-semibold">{label}</div>
      <div className="text-[11px]" style={{ color: t.textMuted }}>{theme === key ? 'Active' : 'Click to apply'}</div>
    </button>
  );
  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="prioris-settings-view">
      <div className="mb-1 text-[10px] font-semibold tracking-wider" style={{ color: t.accent }}>PRIORIS · SETTINGS</div>
      <h2 className="text-xl font-bold mb-6" style={{ color: t.text }}>Display preferences</h2>

      {/* Theme */}
      <section className="mb-6 p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <h3 className="text-[13px] font-bold mb-1" style={{ color: t.text }}>Theme</h3>
        <p className="text-[11px] mb-3" style={{ color: t.textMuted }}>Switch between dark (default) and light. Your preference is stored on this device.</p>
        <div className="flex gap-2">
          {swatch('dark', 'Dark')}
          {swatch('light', 'Light')}
        </div>
      </section>

      {/* Font size */}
      <section className="mb-6 p-4 rounded-lg" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-[13px] font-bold" style={{ color: t.text }}>Font size</h3>
          <div className="text-[12px] font-mono" style={{ color: t.accent }} data-testid="prioris-settings-font-pct">{pct}%</div>
        </div>
        <p className="text-[11px] mb-3" style={{ color: t.textMuted }}>
          Scale every element in the Prioris app. Useful for large 4K monitors (120 %+) or compact laptops (90 %). Applies immediately.
        </p>
        <input
          type="range" min="0.8" max="1.3" step="0.05"
          value={fontScale}
          onChange={e => setFontScale(Number(e.target.value))}
          className="w-full"
          style={{ accentColor: t.accent }}
          data-testid="prioris-settings-font-slider"
        />
        <div className="flex justify-between text-[10px] mt-1" style={{ color: t.textDim }}>
          <span>80%</span><span>90%</span><span>100%</span><span>110%</span><span>120%</span><span>130%</span>
        </div>
        <div className="flex gap-2 mt-3">
          {[0.9, 1, 1.1, 1.15, 1.25].map(v => (
            <button key={v} onClick={() => setFontScale(v)}
              className="px-2.5 py-1 rounded text-[11px]"
              style={{
                background: fontScale === v ? t.accentBg : t.inputBg,
                border: `1px solid ${fontScale === v ? t.accent : t.inputBorder}`,
                color: fontScale === v ? t.accent : t.textMuted,
              }}
              data-testid={`prioris-settings-font-preset-${Math.round(v * 100)}`}>
              {Math.round(v * 100)}%
            </button>
          ))}
          <button onClick={() => setFontScale(1)}
            className="px-2.5 py-1 rounded text-[11px] ml-auto"
            style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.textMuted }}
            data-testid="prioris-settings-font-reset">
            Reset to 100%
          </button>
        </div>
      </section>

      <p className="text-[11px]" style={{ color: t.textDim }}>
        More settings (notifications, default module, export format) coming soon.
      </p>
    </div>
  );
};

export default PriorisApp;
